/**
 * Platform Audit Log Page
 * Shows all platform-level actions and denied access attempts.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  ScrollText,
  ShieldAlert,
  UserPlus,
  Pause,
  Play,
  Settings,
  Filter,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const EVENT_CONFIG = {
  tenant_created: { icon: UserPlus, label: 'Tenant Created', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  tenant_suspended: { icon: Pause, label: 'Tenant Suspended', color: 'text-red-400', bg: 'bg-red-500/10' },
  tenant_activated: { icon: Play, label: 'Tenant Activated', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  tenant_updated: { icon: Settings, label: 'Tenant Updated', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  tenant_features_updated: { icon: Settings, label: 'Features Updated', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  tenant_limits_updated: { icon: Settings, label: 'Limits Updated', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  platform_access_denied: { icon: ShieldAlert, label: 'Access Denied', color: 'text-red-400', bg: 'bg-red-500/10' },
};

const EVENT_TYPES = [
  { value: '', label: 'All Events' },
  { value: 'tenant_created', label: 'Tenant Created' },
  { value: 'tenant_suspended', label: 'Tenant Suspended' },
  { value: 'tenant_activated', label: 'Tenant Activated' },
  { value: 'tenant_updated', label: 'Tenant Updated' },
  { value: 'tenant_features_updated', label: 'Features Updated' },
  { value: 'tenant_limits_updated', label: 'Limits Updated' },
  { value: 'platform_access_denied', label: 'Access Denied' },
];

export default function PlatformAuditPage() {
  const { api } = useAuth();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState('');
  const limit = 25;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (eventFilter) params.append('event_type', eventFilter);
      const { data } = await api.get(`/platform/audit?${params}`);
      setEntries(data.entries || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [page, eventFilter]);

  const getEventConfig = (event) => EVENT_CONFIG[event] || {
    icon: ScrollText,
    label: event,
    color: 'text-zinc-400',
    bg: 'bg-zinc-500/10',
  };

  return (
    <div data-testid="platform-audit-page" className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Audit Log</h1>
        <p className="text-sm text-zinc-500 mt-0.5">{total} platform events recorded</p>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-zinc-500" />
        <select
          value={eventFilter}
          onChange={e => { setEventFilter(e.target.value); setPage(1); }}
          className="bg-zinc-900 border border-zinc-700 text-white text-sm rounded-md px-3 py-1.5"
          data-testid="audit-event-filter"
        >
          {EVENT_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Log Entries */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">No audit entries found</div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry, i) => {
            const config = getEventConfig(entry.event);
            const Icon = config.icon;
            return (
              <div
                key={i}
                className="bg-[#111114] border border-zinc-800/60 rounded-lg p-3 flex items-start gap-3"
                data-testid={`audit-entry-${i}`}
              >
                <div className={`w-8 h-8 rounded-md ${config.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                  <Icon className={`w-4 h-4 ${config.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
                    {entry.tenant_name && (
                      <span className="text-xs text-zinc-400">— {entry.tenant_name}</span>
                    )}
                    {entry.tenant_slug && (
                      <span className="text-[10px] text-zinc-600 font-mono">{entry.tenant_slug}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                    {entry.performed_by && <span>by {entry.performed_by}</span>}
                    {entry.email && entry.event === 'platform_access_denied' && (
                      <span className="text-red-400/70">User: {entry.email} (role: {entry.role}, tenant: {entry.tenant_slug})</span>
                    )}
                    {entry.changes && (
                      <span>Changed: {entry.changes.join(', ')}</span>
                    )}
                  </div>
                </div>
                <div className="text-[10px] text-zinc-600 font-mono whitespace-nowrap shrink-0">
                  {entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-AU', {
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                  }) : '—'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-zinc-600">
            Page {page} of {pages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="border-zinc-700 text-zinc-400 h-7"
            >
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              disabled={page === pages}
              className="border-zinc-700 text-zinc-400 h-7"
            >
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
